// frontend/src/pages/Orders.tsx

import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import api from '@/lib/api';
import { AlertCircle, Clock, CheckCircle, XCircle, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Order {
  id: number;
  order_type: 'buy' | 'sell';
  asset_id: string;
  amount_btc: number;          // ← CHANGED
  price_usd?: number;           // ← CHANGED
  price_type?: string;          // ← NEW
  dsa_enabled?: boolean;        // ← NEW
  dsa_spread?: number;          // ← NEW
  status: 'active' | 'completed' | 'cancelled' | 'expired' | 'partially_filled';  // ← ADDED partially_filled
  created_at: string;
  updated_at: string;
  description?: string;         // ← NEW
}

export function Orders() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all');
  

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, filter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/orders', {
        params: { status: filter === 'all' ? undefined : filter }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId: number) => {
    if (!confirm('Are you sure you want to cancel this order?')) return;
    
    try {
      await api.delete(`/orders/${orderId}`);
      fetchOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert('Failed to cancel order');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-apple-blue-600 bg-apple-blue-100';
      case 'completed': return 'text-apple-green-600 bg-apple-green-100';
      case 'cancelled': return 'text-apple-gray-600 bg-apple-gray-100';
      case 'expired': return 'text-apple-orange-600 bg-apple-orange-100';
      default: return 'text-apple-gray-600 bg-apple-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Clock className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'expired': return <AlertCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container-main py-12">
        <div className="max-w-2xl mx-auto text-center">
          <AlertCircle className="w-16 h-16 text-apple-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">Authentication Required</h2>
          <p className="text-apple-gray-600 mb-6">
            Please log in to view your orders.
          </p>
          <Button variant="primary" onClick={() => window.location.href = '/login'}>
            Log In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-main py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
         <h1 className="text-4xl font-bold mb-2">{t('orders.title')}</h1>
<p className="text-apple-gray-600">
  {t('orders.subtitle')}
</p> 
        </div>

        {/* Filters */}
<Card className="mb-6">
  <CardContent className="p-4">
    <div className="flex items-center gap-2">
      <Filter className="w-5 h-5 text-apple-gray-600" />
      <span className="font-medium mr-4">{t('orders.filter')}</span>
      <div className="flex gap-2">
        {(['all', 'active', 'completed', 'cancelled'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === f
                ? 'bg-apple-blue-500 text-white'
                : 'bg-apple-gray-100 text-apple-gray-700 hover:bg-apple-gray-200'
            }`}
          >
            {t(`orders.${f}`)}
          </button>
        ))}
      </div>
    </div>
  </CardContent>
</Card>

        {/* Orders List */}
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-apple-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-apple-gray-600">Loading orders...</p>
            </CardContent>
          </Card>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <AlertCircle className="w-16 h-16 text-apple-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Orders Found</h3>
              <p className="text-apple-gray-600 mb-6">
                {filter === 'all' 
                  ? "You haven't created any orders yet."
                  : `No ${filter} orders found.`}
              </p>
              <Button variant="primary" onClick={() => window.location.href = '/trade'}>
                Create Your First Order
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          order.order_type === 'buy' 
                            ? 'bg-apple-green-100 text-apple-green-700'
                            : 'bg-apple-red-100 text-apple-red-700'
                        }`}>
                          {order.order_type.toUpperCase()}
                        </span>
                        <span className="text-lg font-semibold">
                          {order.asset_id.toUpperCase()}
                        </span>
                        <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                          {getStatusIcon(order.status)}
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-apple-gray-600">Amount:</span>
                          <div className="font-semibold">
                             {Number(order.amount_btc).toFixed(8)} BTC
                          </div>
                        </div>
                        {order.price_usd && (
                          <div>
                            <span className="text-apple-gray-600">Price:</span>
                            <div className="font-semibold">
                              ${Number(order.price_usd).toFixed(2)}
                            </div>
                          </div>
)}
                        <div>
                          <span className="text-apple-gray-600">Created:</span>
                          <div className="font-semibold">
                            {new Date(order.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div>
                          <span className="text-apple-gray-600">Order ID:</span>
                          <div className="font-semibold font-mono text-xs">
                            #{order.id}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
